<template>
  <MainLayout>
    <DetailForm
      title="Edit"
      description="This form will edit a candidate"
      action="Save"
      @handle-cancel="router.go(-1)"
      @handle-action="handleSubmit"
    >
      <ContactForm v-if="showEdit" v-model="contact" />
    </DetailForm>
  </MainLayout>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { toast } from 'vue-sonner';
import MainLayout from '@/components/layouts/MainLayout/index.vue';
import DetailForm from '../shared/DetailForm.vue';
import ContactForm from './ContactForm.vue';
import DateUtility from '@/utilities/dates';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';
import RailsUtility from '@/utilities/rails';

const router = useRouter();
const route = useRoute();
const loadingSpinnerStore = useLoadingSpinnerStore();

const showEdit = ref(false);
const contact = ref({
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  stage: '',
  licenseStatus: '',
  reLicenseNumber: '',
  phoneScreenAt: '',
  interviewAt: '',
});

const getContact = (): void => {
  loadingSpinnerStore.showLoadingSpinner();
  fetch(`/api/contacts/${route.params.id}`)
    .then(response => {
      loadingSpinnerStore.hideLoadingSpinner();
      return response.json();
    })
    .then(data => {
      contact.value.firstName = data.record.first_name;
      contact.value.lastName = data.record.last_name;
      contact.value.phone = data.record.phone_number;
      contact.value.email = data.record.email;
      contact.value.stage = data.record.stage;
      contact.value.licenseStatus = RailsUtility.convertFromCasedEnumFormat(data.record.license_status);
      contact.value.reLicenseNumber = data.record.re_license_number;
      contact.value.phoneScreenAt = DateUtility.formatDate(data.record.phone_screen_at, "yyyy-MM-dd'T'HH:mm") ?? '';
      contact.value.interviewAt = DateUtility.formatDate(data.record.interview_at, "yyyy-MM-dd'T'HH:mm") ?? '';
      showEdit.value = true;
    });
};

const handleSubmit = (): void => {
  loadingSpinnerStore.showLocalLoadingSpinner();
  fetch(`/api/contacts/${route.params.id}`, {
    method: 'PUT',
    body: JSON.stringify({
      first_name: contact.value.firstName,
      last_name: contact.value.lastName,
      phone_number: contact.value.phone,
      email: contact.value.email,
      stage: contact.value.stage,
      license_status: RailsUtility.convertToCasedEnumFormat(contact.value.licenseStatus),
      re_license_number: contact.value.reLicenseNumber,
      phone_screen_at: contact.value.phoneScreenAt ? new Date(contact.value.phoneScreenAt).toISOString() : null,
      interview_at: contact.value.interviewAt ? new Date(contact.value.interviewAt).toISOString() : null,
    }),
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': document.querySelectorAll<HTMLMetaElement>('[name="csrf-token"]')[0].content,
    },
  })
    .then(response => response.json())
    .then(async data => {
      loadingSpinnerStore.hideLocalLoadingSpinner();
      if (data.status != 'error') {
        const targetRoute = `/contacts/${route.params.id}`;
        if (route.path !== targetRoute) {
          await router.push(targetRoute);
        }
        toast.success('Candidate edited', {
          action: { label: 'Dismiss' },
          description: 'Your candidate was successfully edited.  Use the button below to dismiss this alert.',
        });
      } else {
        toast.error('Candidate edit error', {
          action: { label: 'Dismiss' },
          description: data.message,
        });
      }
    });
};

onMounted(getContact);
</script>
